import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { motion } from "framer-motion";
import ContactForm from "../components/ContactForm";

const MotionBox = motion(Box);

const INK = "#2B211C";
const MUTED = "#75675E";
const ACCENT = "#A06E50";
const PAPER = "#F7F4EF";
const LINE = "#DCD4CC";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const collaborationTypes = [
  {
    number: "01",
    title: "Website",
    description:
      "A considered digital presence for a brand, business, creator, or organisation.",
  },
  {
    number: "02",
    title: "Web Application",
    description:
      "Custom digital products built around real workflows, users, and business requirements.",
  },
  {
    number: "03",
    title: "Mobile & PWA",
    description:
      "Responsive experiences that extend your product beyond the browser.",
  },
  {
    number: "04",
    title: "Brand & Design",
    description:
      "Visual systems, interfaces, landing pages, and digital design that give your idea a clear identity.",
  },
  {
    number: "05",
    title: "SEO & Growth",
    description:
      "A stronger technical and content foundation for discoverability and long-term growth.",
  },
  {
    number: "06",
    title: "Maintenance",
    description:
      "Ongoing improvements, monitoring, updates, and support after launch.",
  },
];

const process = [
  {
    number: "01",
    title: "Discover",
    text: "We understand the idea, the audience, the problem, and what success should look like.",
  },
  {
    number: "02",
    title: "Define",
    text: "We turn the conversation into a clear scope, priorities, structure, and direction.",
  },
  {
    number: "03",
    title: "Design",
    text: "We shape the visual language and experience before moving into the build.",
  },
  {
    number: "04",
    title: "Build",
    text: "We develop the product with attention to performance, responsiveness, and detail.",
  },
  {
    number: "05",
    title: "Launch",
    text: "We prepare the final experience, deployment, domains, and everything needed to go live.",
  },
  {
    number: "06",
    title: "Grow",
    text: "After launch, we can continue refining, maintaining, and expanding the product.",
  },
];

const expectations = [
  "A clear understanding of your goals",
  "A practical project scope",
  "Transparent communication",
  "Design and development aligned together",
  "Responsive, production-ready experiences",
  "Support beyond the initial launch",
];

export default function WorkTogether() {
  const scrollToBrief = () => {
    document
      .getElementById("project-brief")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: PAPER,
        color: INK,
        pt: { xs: 9, md: 13 },
        pb: 10,
        overflow: "hidden",
        "& .editorial-serif": {
          fontFamily: "Georgia, 'Times New Roman', serif",
        },
      }}
    >
      <Container maxWidth="xl">
        {/* HERO */}
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
          sx={{
            minHeight: { md: "76vh" },
            display: "flex",
            alignItems: "flex-end",
            pb: { xs: 8, md: 12 },
          }}
        >
          <Grid container spacing={{ xs: 5, md: 9 }} alignItems="flex-end">
            <Grid item xs={12} md={8}>
              <Chip
                label="Work Together"
                variant="outlined"
                sx={{
                  mb: 3,
                  borderColor: INK,
                  color: INK,
                  borderRadius: 999,
                  bgcolor: "transparent",
                  px: 1.5,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: ".16em",
                  textTransform: "uppercase",
                }}
              />

              <Typography
                component="h1"
                className="editorial-serif"
                sx={{
                  fontSize: {
                    xs: "3.3rem",
                    sm: "4.8rem",
                    md: "7.4rem",
                  },
                  lineHeight: { xs: 0.97, md: 0.88 },
                  letterSpacing: "-.065em",
                  fontWeight: 700,
                  maxWidth: 1050,
                }}
              >
                Let’s build something worth making.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ borderTop: `1px solid ${LINE}`, pt: 2 }}>
                <Typography
                  sx={{
                    color: MUTED,
                    fontSize: { xs: 14, md: 15 },
                    lineHeight: 1.85,
                    maxWidth: 390,
                  }}
                >
                  Have an idea, a business, or a product that needs a digital
                  home? Bring us the problem. We’ll work with you to turn it
                  into something clear, useful, and beautifully built.
                </Typography>

                <Button
                  onClick={scrollToBrief}
                  endIcon={<ArrowDownwardRoundedIcon />}
                  sx={{
                    mt: 4,
                    px: 0,
                    color: INK,
                    textTransform: "none",
                    fontWeight: 800,
                    "&:hover": {
                      bgcolor: "transparent",
                      color: ACCENT,
                    },
                  }}
                >
                  Start a conversation
                </Button>
              </Box>
            </Grid>
          </Grid>
        </MotionBox>

        <Divider sx={{ borderColor: LINE }} />

        {/* INTRO */}
        <Box sx={{ py: { xs: 8, md: 12 } }}>
          <Grid container spacing={{ xs: 5, md: 10 }}>
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontSize: 10,
                  letterSpacing: ".2em",
                  textTransform: "uppercase",
                  fontWeight: 800,
                  color: ACCENT,
                }}
              >
                01 — The collaboration
              </Typography>

              <Typography
                className="editorial-serif"
                sx={{
                  mt: 1.5,
                  fontSize: { xs: "2.8rem", md: "4.5rem" },
                  lineHeight: 0.96,
                  letterSpacing: "-.055em",
                }}
              >
                Good work starts with a good conversation.
              </Typography>
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography
                sx={{
                  color: MUTED,
                  fontSize: { xs: 16, md: 19 },
                  lineHeight: 1.85,
                  maxWidth: 760,
                }}
              >
                We don’t believe every project needs the same process or the
                same technology. We start by understanding what you are
                trying to achieve, then build the right digital experience
                around it.
              </Typography>

              <Typography
                sx={{
                  color: MUTED,
                  lineHeight: 1.85,
                  maxWidth: 700,
                  mt: 3,
                }}
              >
                Whether you are starting from an idea, redesigning an
                existing product, or looking for a team to take care of the
                details, the goal stays the same: make something useful,
                distinctive, and ready for the real world.
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* WAYS TO WORK */}
        <Box
          sx={{
            py: { xs: 7, md: 11 },
            borderTop: `1px solid ${LINE}`,
          }}
        >
          <MotionBox
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={{ mb: 7 }}
          >
            <Typography
              sx={{
                fontSize: 10,
                letterSpacing: ".2em",
                textTransform: "uppercase",
                fontWeight: 800,
                color: ACCENT,
              }}
            >
              02 — Ways we can work
            </Typography>

            <Typography
              className="editorial-serif"
              sx={{
                mt: 1.5,
                fontSize: { xs: "2.8rem", md: "5rem" },
                lineHeight: 0.94,
                letterSpacing: "-.055em",
              }}
            >
              Bring us the idea.
            </Typography>
          </MotionBox>

          <Grid container spacing={0}>
            {collaborationTypes.map((item, index) => (
              <Grid item xs={12} md={6} key={item.title}>
                <MotionBox
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.1 }}
                  custom={index * 0.05}
                  sx={{
                    minHeight: { xs: 150, md: 185 },
                    borderTop: `1px solid ${LINE}`,
                    display: "grid",
                    gridTemplateColumns: "55px 1fr",
                    gap: 2,
                    pt: 3,
                    pr: { md: index % 2 === 0 ? 7 : 0 },
                    mr: { md: index % 2 === 0 ? 4 : 0 },
                    transition: "background-color .25s ease",
                    "&:hover": {
                      bgcolor: "rgba(160,110,80,.055)",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: ACCENT,
                      fontWeight: 800,
                    }}
                  >
                    {item.number}
                  </Typography>

                  <Box>
                    <Typography
                      className="editorial-serif"
                      sx={{
                        fontSize: { xs: 23, md: 29 },
                        letterSpacing: "-.025em",
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      sx={{
                        color: MUTED,
                        fontSize: 14,
                        lineHeight: 1.75,
                        maxWidth: 480,
                        mt: 1.5,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </MotionBox>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* PROCESS */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            borderTop: `1px solid ${LINE}`,
          }}
        >
          <Grid container spacing={{ xs: 6, md: 10 }}>
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontSize: 10,
                  letterSpacing: ".2em",
                  textTransform: "uppercase",
                  fontWeight: 800,
                  color: ACCENT,
                }}
              >
                03 — The process
              </Typography>

              <Typography
                className="editorial-serif"
                sx={{
                  mt: 1.5,
                  fontSize: { xs: "2.8rem", md: "4.8rem" },
                  lineHeight: 0.95,
                  letterSpacing: "-.055em",
                }}
              >
                From first
                <br />
                thought to
                <br />
                launch.
              </Typography>
            </Grid>

            <Grid item xs={12} md={8}>
              {process.map((step, index) => (
                <MotionBox
                  key={step.number}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.12 }}
                  custom={index * 0.04}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "60px 1fr",
                    gap: 2,
                    py: 3.2,
                    borderTop: `1px solid ${LINE}`,
                  }}
                >
                  <Typography
                    sx={{
                      color: ACCENT,
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    {step.number}
                  </Typography>

                  <Box>
                    <Typography
                      className="editorial-serif"
                      sx={{
                        fontSize: { xs: 22, md: 27 },
                        letterSpacing: "-.02em",
                      }}
                    >
                      {step.title}
                    </Typography>

                    <Typography
                      sx={{
                        color: MUTED,
                        fontSize: 14,
                        lineHeight: 1.8,
                        maxWidth: 620,
                        mt: 1,
                      }}
                    >
                      {step.text}
                    </Typography>
                  </Box>
                </MotionBox>
              ))}
            </Grid>
          </Grid>
        </Box>

        {/* WHAT YOU CAN EXPECT */}
        <Box
          sx={{
            bgcolor: INK,
            color: PAPER,
            px: { xs: 3, md: 7 },
            py: { xs: 7, md: 10 },
          }}
        >
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontSize: 10,
                  letterSpacing: ".2em",
                  textTransform: "uppercase",
                  fontWeight: 800,
                  color: "#C9A78F",
                }}
              >
                04 — What you can expect
              </Typography>

              <Typography
                className="editorial-serif"
                sx={{
                  mt: 2,
                  fontSize: { xs: "2.8rem", md: "5rem" },
                  lineHeight: 0.94,
                  letterSpacing: "-.055em",
                  maxWidth: 700,
                }}
              >
                A collaboration that stays clear from start to finish.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={0}>
                {expectations.map((item, index) => (
                  <Box
                    key={item}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 2,
                      borderTop: "1px solid rgba(247,244,239,.18)",
                    }}
                  >
                    <CheckRoundedIcon
                      sx={{
                        fontSize: 17,
                        color: "#C9A78F",
                      }}
                    />
                    <Typography
                      sx={{
                        color: "rgba(247,244,239,.76)",
                        fontSize: 14,
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* PROJECT BRIEF */}
        <Box
          id="project-brief"
          sx={{
            py: { xs: 8, md: 12 },
            scrollMarginTop: 90,
          }}
        >
          <MotionBox
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            sx={{ mb: 6 }}
          >
            <Typography
              sx={{
                fontSize: 10,
                letterSpacing: ".2em",
                textTransform: "uppercase",
                fontWeight: 800,
                color: ACCENT,
              }}
            >
              05 — Project brief
            </Typography>

            <Typography
              className="editorial-serif"
              sx={{
                mt: 1.5,
                fontSize: { xs: "2.8rem", md: "5rem" },
                lineHeight: 0.94,
                letterSpacing: "-.055em",
                maxWidth: 950,
              }}
            >
              Tell us what you’re building.
            </Typography>

            <Typography
              sx={{
                mt: 2.5,
                color: MUTED,
                lineHeight: 1.8,
                maxWidth: 650,
              }}
            >
              You don’t need a perfect brief. Give us the context, what you
              need, and where you want to go. We’ll take it from there.
            </Typography>
          </MotionBox>

          <ContactForm />
        </Box>

        {/* FINAL CTA */}
        <Box
          sx={{
            borderTop: `1px solid ${LINE}`,
            pt: { xs: 7, md: 10 },
            pb: 3,
          }}
        >
          <Grid container spacing={4} alignItems="end">
            <Grid item xs={12} md={8}>
              <Typography
                className="editorial-serif"
                sx={{
                  fontSize: { xs: "2.5rem", md: "4.4rem" },
                  lineHeight: 0.95,
                  letterSpacing: "-.055em",
                }}
              >
                Have an idea?
                <br />
                Let’s talk about it.
              </Typography>
            </Grid>

<Grid item xs={12} md={4}>
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: { xs: "stretch", sm: "flex-start" },
      gap: 2,
    }}
  >
    {/* Primary Action Button */}
    <Button
      onClick={scrollToBrief}
      variant="contained"
      endIcon={<ArrowForwardRoundedIcon />}
      sx={{
        bgcolor: INK,
        color: PAPER,
        borderRadius: "8px", // Clean, modern rounded corners (or set to 0 for strict brutalism)
        px: 4,
        py: 1.8,
        width: { xs: "100%", sm: "fit-content" },
        textTransform: "none",
        fontWeight: 750,
        fontSize: "0.92rem",
        boxShadow: "none",
        transition: "background-color 250ms ease, transform 250ms ease",
        "&:hover": {
          bgcolor: ACCENT,
          boxShadow: "none",
          transform: "translateY(-2px)",
        },
      }}
    >
      Start your project
    </Button>

    {/* Secondary Link Button */}
    <Button
      component={RouterLink}
      to="/contact"
      endIcon={<NorthEastRoundedIcon />}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        p: 0,
        minWidth: 0,
        width: "fit-content",
        color: MUTED,
        textTransform: "none",
        fontWeight: 650,
        fontSize: "0.85rem",
        transition: "color 200ms ease",
        "&:hover": {
          bgcolor: "transparent",
          color: INK,
          "& .MuiButton-endIcon": {
            transform: "translate(2px, -2px)",
          },
        },
        "& .MuiButton-endIcon": {
          transition: "transform 200ms ease",
        },
      }}
    >
      Contact page
    </Button>
  </Box>
</Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
